import { createClient, SupabaseClient } from '@supabase/supabase-js';

// Module-level client instance
let _supabase: SupabaseClient | null = null;

// Initialize the Supabase client - must be called before using supabase
export function initSupabase(url: string, anonKey: string): SupabaseClient {
  if (_supabase) {
    return _supabase;
  }

  _supabase = createClient(url, anonKey, {
    realtime: {
      params: {
        eventsPerSecond: 10,
      },
    },
  });

  return _supabase;
}

// Get the Supabase client - throws if not initialized
export function getSupabaseClient(): SupabaseClient {
  if (!_supabase) {
    throw new Error(
      'Supabase client not initialized. Call initSupabase() in your app entry point.'
    );
  }
  return _supabase;
}

// Export supabase as a getter for backward compatibility
export const supabase = new Proxy({} as SupabaseClient, {
  get(_, prop) {
    const client = getSupabaseClient();
    const value = client[prop as keyof SupabaseClient];
    if (typeof value === 'function') {
      return value.bind(client);
    }
    return value;
  },
});

// Helper to get site by slug
export async function getSiteBySlug(slug: string) {
  const { data, error } = await getSupabaseClient()
    .from('sites')
    .select('*')
    .eq('slug', slug)
    .single();

  if (error) throw error;
  return data;
}

// Helper to get active race for a site
export async function getActiveRace(siteId: string) {
  const { data, error } = await getSupabaseClient()
    .from('races')
    .select(`
      *,
      circuit:circuits(*),
      participants:race_participants(
        *,
        boat:boats(*)
      )
    `)
    .eq('site_id', siteId)
    .in('status', ['pending', 'countdown', 'active'])
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
  return data;
}

// Helper to get boats for a site
export async function getBoats(siteId: string) {
  const { data, error } = await getSupabaseClient()
    .from('boats')
    .select('*')
    .eq('site_id', siteId)
    .order('number');

  if (error) throw error;
  return data;
}

// Helper to get leaderboard for a race
export async function getLeaderboard(raceId: string) {
  const { data, error } = await getSupabaseClient()
    .from('leaderboard_live')
    .select('*')
    .eq('race_id', raceId)
    .order('position');

  if (error) throw error;
  return data;
}

// Helper to get daily best laps
export async function getDailyBestLaps(siteId: string, limit = 10) {
  const { data, error } = await getSupabaseClient()
    .from('daily_best_laps')
    .select('*')
    .eq('site_id', siteId)
    .order('best_lap_ms')
    .limit(limit);

  if (error) throw error;
  return data;
}

// Helper to get queue for a site
export async function getQueue(siteId: string) {
  const { data, error } = await getSupabaseClient()
    .from('queue_ordered')
    .select('*')
    .eq('site_id', siteId)
    .order('position_in_queue');

  if (error) throw error;
  return data;
}
